import React, { useState, useContext } from 'react';
import { Box, Button, Typography, Paper, styled } from '@mui/material';
import { OrgContext } from '../contexts/OrgContext';
import { useNavigate } from 'react-router-dom';
import { bulkCreateProducts } from '../services/productService';
import Papa from 'papaparse';
import TanStackTable from '../components/TanStackTable';
import BackButton from '../components/BackButton';  // Add this import

const HiddenInput = styled('input')({
  display: 'none',
});

const ProductsBulkImport = () => {
  const { orgId } = useContext(OrgContext);
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null); // Store bulk import results

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleDownloadTemplate = () => {
    const csvContent = 'data:text/csv;charset=utf-8,'
      + 'product_name,category,unit_of_measure,scanner_reference,sub_category,type_of_inventory,company,sku,current_unit_cost,current_unit_price\n'
      + 'iPhone 15 Pro,Electronics,piece,IPHONE15PRO-256,Smartphones,permanent,Apple Inc,APL-IP15P-256,999.00,1299.00\n'
      + 'Organic Bananas,Food,kg,BAN-ORG-001,Fresh Produce,perishing,Fresh Foods Ltd,FF-BAN-001,1.50,2.99\n'
      + 'Cotton T-Shirt,Apparel,piece,,,permanent,Fashion Basics,FB-TSH-001,15.00,29.99\n'
      + 'Hand Sanitizer,Health,ml,SAN-500-ABC,Personal Care,permanent,Health Essentials,HE-SAN-001,3.50,7.99\n';
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'products_template.csv');
    document.body.appendChild(link);
    link.click();
  };

  const handleUpload = () => {
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }
    setError(null);

    Papa.parse(file, {
      header: true,
      complete: async (parseResults) => {
        const products = parseResults.data.map((product, index) => ({
          product_name: product.product_name,
          category: product.category,
          unit_of_measure: product.unit_of_measure,
          sub_category: product.sub_category,
          scanner_reference: product.scanner_reference,
          type_of_inventory: product.type_of_inventory?.toLowerCase(),
          company: product.company,
          sku: product.sku,
          current_unit_cost: parseFloat(product.current_unit_cost),
          current_unit_price: parseFloat(product.current_unit_price),
          org_id: orgId,
          row: index + 2, // Add row number for error tracking (1 for header, so start from 2)
        }));

        try {
          const response = await bulkCreateProducts(products);
          setResults(response);
        } catch (err) {
          setError('Error uploading products.');
          console.error(err);
        }
      },
      error: (err, file, inputElem, reason) => {
        setError(`Error parsing CSV file at row ${err.row}: ${reason}`);
        console.error(err);
      },
    });
  };

  const handleDone = () => {
    navigate(`/${orgId}/products`);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <BackButton />  {/* Add BackButton here */}
      <Typography variant="h5" gutterBottom>
        Bulk Import Products
      </Typography>
      <Typography variant="body1" gutterBottom>
        Follow the instructions below to bulk import products:
      </Typography>
      <Typography variant="body2" gutterBottom>
        1. Download the template CSV file.
      </Typography>
      <Typography variant="body2" gutterBottom>
        2. Fill in the product details in the CSV file. Ensure no extra rows are present, as they will lead to failures during the upload.
      </Typography>
      <Typography variant="body2" gutterBottom>
        3. Required fields: product_name, category, unit_of_measure, company, sku, current_unit_cost, current_unit_price.
      </Typography>
      <Typography variant="body2" gutterBottom>
        4. Optional fields: scanner_reference, sub_category, type_of_inventory.
      </Typography>
      <Typography variant="body2" gutterBottom>
        5. Acceptable values for type_of_inventory: "permanent", "perishing".
      </Typography>
      <Typography variant="body2" gutterBottom>
        6. Upload the completed CSV file and start bulk import.
      </Typography>

      <Button variant="contained" onClick={handleDownloadTemplate} sx={{ my: 2, mr: 2 }}>
        Download Template CSV
      </Button>

      <label htmlFor="contained-button-file">
        <HiddenInput
          accept=".csv"
          id="contained-button-file"
          type="file"
          onChange={handleFileChange}
        />
        <Button variant="contained" component="span">
          Select CSV File
        </Button>
      </label>

      {file && (
        <Typography variant="body2" sx={{ mt: 2 }}>
          Selected File: {file.name}
        </Typography>
      )}

      <Box sx={{ mt: 2 }}>
        <Button variant="contained" onClick={handleUpload}>
          Start Bulk Import
        </Button>
      </Box>

      {error && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      {results && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Bulk Import Results
          </Typography>
          <Typography sx={{ mb: 3,color: "#ddd" }} variant="body2" gutterBottom>
            Scroll down to see the successful and failed imports.
          </Typography>
          <Typography variant="body2" gutterBottom>
            Successful Imports
          </Typography>
          <Box sx={{ mb: 2 }}>
            <TanStackTable
              data={results.successes || []}
              columns={[
                { header: 'ID', accessorKey: 'id' },
                { header: 'Product Name', accessorKey: 'product_name' },
                { header: 'Company', accessorKey: 'company' },
                { header: 'SKU', accessorKey: 'sku' },
                { header: 'Unit Cost', accessorKey: 'current_unit_cost' },
                { header: 'Unit Price', accessorKey: 'current_unit_price' },
              ]}
            />
          </Box>
          <Typography variant="body2" gutterBottom>
            Failed Imports
          </Typography>
          <Box>
            <TanStackTable
              data={(results.failures || []).map((f) => ({
                ...f.dto,
                error: f.error,
                row: f.dto.row, // Include row number in the failure data
              }))}
              columns={[
                { header: 'Row', accessorKey: 'row' },
                { header: 'Product Name', accessorKey: 'product_name' },
                { header: 'Company', accessorKey: 'company' },
                { header: 'SKU', accessorKey: 'sku' },
                { header: 'Error', accessorKey: 'error' },
              ]}
            />
          </Box>
          <Button variant="contained" onClick={handleDone} sx={{ mt: 2 }}>
            Done
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default ProductsBulkImport;
